/**
 * @fileoverview Universal Credits Hook (Stub)
 * 
 * Placeholder while pricing is being redesigned.
 * Everything returns unlimited/true — no DB queries, no gating.
 */

import { useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';

// ============================================================================
// Types (kept for interface compatibility)
// ============================================================================

export type SubscriptionTier = 'free' | 'plus' | 'max';

export interface TierLimits {
  monthlyCredits: number;
  savedItems: number;
  priceAlerts: number;
  collections: number;
  monthlyAnimationCredits: number;
  dailyTryOns: number;
  trackedItems: number;
}

export type FeatureType = 'tryon' | 'animation' | 'visual_search' | 'price_check';
export type LimitType = keyof TierLimits;

// ============================================================================
// Unlimited limits stub
// ============================================================================

const UNLIMITED_LIMITS: TierLimits = {
  monthlyCredits: -1,
  savedItems: -1,
  priceAlerts: -1,
  collections: -1,
  monthlyAnimationCredits: -1,
  dailyTryOns: -1,
  trackedItems: -1,
};

// ============================================================================
// Hook
// ============================================================================

export function useUniversalCredits() {
  const { user } = useAuth();

  // Environment-aware domain for referral links
  const appDomain = import.meta.env.MODE === 'production'
    ? 'https://wntd.ai'
    : import.meta.env.MODE === 'staging'
    ? 'https://wntd.ai'
    : 'https://seyu.lovable.app';

  const canUseFeature = useCallback((_featureType: FeatureType): boolean => true, []);
  const getRemainingUsage = useCallback((_featureType: FeatureType): number | null => null, []);
  const checkLimit = useCallback((_limitType: LimitType, _currentCount: number): boolean => true, []);
  const hasCredits = useCallback((_required: number = 1): boolean => true, []);
  const getTotalCredits = useCallback((): number => Infinity, []);
  const processReferral = useCallback(async (_code: string) => ({ success: true }), []);

  return {
    // Loading
    isLoading: false,

    // Subscription info — everything unlimited
    tier: 'free' as SubscriptionTier,
    subscription: null,
    limits: UNLIMITED_LIMITS,
    tierInfo: { name: 'Free', tagline: '', monthlyPrice: 0, yearlyPrice: 0, price: 0, limits: UNLIMITED_LIMITS, features: [] },
    isPremium: false,
    isUnlimited: true,
    isFreeMode: true,

    // Credits — not tracked
    credits: null,
    referralLink: user ? `${appDomain}/?ref=` : null,
    totalCredits: Infinity,
    dailyRemaining: Infinity,
    bonusCredits: 0,
    purchasedCredits: 0,

    // Feature usage — not tracked
    featureUsage: [],

    // Actions — no-ops
    useCredit: async (_amount?: number) => ({ success: true as const }),
    isUsingCredit: false,
    incrementUsage: (_featureType: FeatureType) => {},
    processReferral,
    refetchCredits: async () => ({ data: null, error: null }),

    // Helpers — always allow
    canUseFeature,
    getRemainingUsage,
    checkLimit,
    hasCredits,
    getTotalCredits,
  };
}
